import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

/**
 * Standings table for a single group in a groups_knockout tournament.
 * Pure presentation — derives standings from the supplied match list.
 */
export default function GroupTable({ group, matches, players }) {
  const playerMap = {};
  players.forEach((p) => { playerMap[p.id] = p; });

  // Initialise standings for every group participant
  const standings = {};
  group.player_ids.forEach((pid) => {
    standings[pid] = { played: 0, won: 0, lost: 0, drawn: 0, frames_won: 0, frames_lost: 0, points: 0 };
  });

  const groupMatches = matches.filter(
    (m) => group.player_ids.includes(m.player1_id)
      && group.player_ids.includes(m.player2_id)
      && ["completed", "walkover"].includes(m.status),
  );

  groupMatches.forEach((m) => {
    const p1 = m.player1_id;
    const p2 = m.player2_id;
    if (standings[p1]) {
      standings[p1].played++;
      standings[p1].frames_won += m.player1_score || 0;
      standings[p1].frames_lost += m.player2_score || 0;
    }
    if (standings[p2]) {
      standings[p2].played++;
      standings[p2].frames_won += m.player2_score || 0;
      standings[p2].frames_lost += m.player1_score || 0;
    }
    if (m.winner_id === p1 && standings[p1]) {
      standings[p1].won++;
      standings[p1].points += 3;
      if (standings[p2]) standings[p2].lost++;
    } else if (m.winner_id === p2 && standings[p2]) {
      standings[p2].won++;
      standings[p2].points += 3;
      if (standings[p1]) standings[p1].lost++;
    } else {
      if (standings[p1]) { standings[p1].drawn++; standings[p1].points++; }
      if (standings[p2]) { standings[p2].drawn++; standings[p2].points++; }
    }
  });

  const sorted = Object.entries(standings)
    .map(([pid, s]) => ({
      pid,
      name: playerMap[pid]?.name || "Unknown",
      ...s,
      frame_diff: s.frames_won - s.frames_lost,
    }))
    .sort((a, b) => b.points - a.points || b.frame_diff - a.frame_diff);

  return (
    <div className="mb-6" data-testid={`group-table-${group.name}`}>
      <h3 className="font-heading font-semibold mb-2 text-primary">{group.name}</h3>
      <Table>
        <TableHeader>
          <TableRow className="border-border">
            <TableHead className="text-muted-foreground">#</TableHead>
            <TableHead className="text-muted-foreground">Player</TableHead>
            <TableHead className="text-center text-muted-foreground">P</TableHead>
            <TableHead className="text-center text-muted-foreground">W</TableHead>
            <TableHead className="text-center text-muted-foreground">L</TableHead>
            <TableHead className="text-center text-muted-foreground">FW</TableHead>
            <TableHead className="text-center text-muted-foreground">FL</TableHead>
            <TableHead className="text-center text-muted-foreground">FD</TableHead>
            <TableHead className="text-center text-muted-foreground font-bold">Pts</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((s, i) => (
            <TableRow key={s.pid} className="border-border hover:bg-white/5">
              <TableCell className="font-medium">{i + 1}</TableCell>
              <TableCell>
                <Link to={`/players/${s.pid}`} className="hover:text-primary transition-colors font-medium">{s.name}</Link>
              </TableCell>
              <TableCell className="text-center">{s.played}</TableCell>
              <TableCell className="text-center text-primary font-medium">{s.won}</TableCell>
              <TableCell className="text-center text-destructive">{s.lost}</TableCell>
              <TableCell className="text-center">{s.frames_won}</TableCell>
              <TableCell className="text-center">{s.frames_lost}</TableCell>
              <TableCell className="text-center">{s.frame_diff >= 0 ? `+${s.frame_diff}` : s.frame_diff}</TableCell>
              <TableCell className="text-center font-bold text-secondary">{s.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
